import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { api } from "../api";
import { DataTable } from "../components/DataTable";
import type { Store } from "../types";

const CHANNEL_BADGE: Record<string, string> = {
  "直營門市": "bg-blue-50 text-blue-700",
  "量販通路": "bg-amber-50 text-amber-700",
  "便利商店": "bg-emerald-50 text-emerald-700",
  "電商": "bg-purple-50 text-purple-700",
};

export function StoreList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["stores", search],
    queryFn: () => api.listStores({ search: search || undefined, per_page: 200 }),
  });

  const createMutation = useMutation({
    mutationFn: (d: Partial<Store>) => api.createStore(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      setShowForm(false);
    },
  });

  const columns = [
    { key: "name", label: "門市/通路名稱", sortable: true, filterable: true, render: (s: Store) => (
      <span className="font-medium text-slate-900">{s.name}</span>
    )},
    { key: "region", label: "區域", sortable: true, filterable: true, width: "5rem" },
    { key: "city", label: "城市", sortable: true, filterable: true, width: "6rem" },
    { key: "channel", label: "通路類型", sortable: true, filterable: true, width: "7rem", render: (s: Store) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHANNEL_BADGE[s.channel ?? ""] ?? "bg-slate-50 text-slate-500"}`}>
        {s.channel ?? "-"}
      </span>
    )},
  ];

  return (
    <div className="max-w-5xl space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">通路</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={16} /> 新增
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="搜尋門市..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {showForm && (
        <form
          className="rounded-xl border border-slate-200 bg-white p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            createMutation.mutate({
              name: fd.get("name") as string,
              region: (fd.get("region") as string) || undefined,
              city: (fd.get("city") as string) || undefined,
              channel: (fd.get("channel") as string) || undefined,
            });
          }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <input name="name" className="input" placeholder="門市名稱 *" required />
            <input name="region" className="input" placeholder="區域" />
            <input name="city" className="input" placeholder="城市" />
            <select name="channel" className="input">
              <option value="">通路類型</option>
              <option value="直營門市">直營門市</option>
              <option value="量販通路">量販通路</option>
              <option value="便利商店">便利商店</option>
              <option value="電商">電商</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
              儲存
            </button>
            <button type="button" className="btn" onClick={() => setShowForm(false)}>
              取消
            </button>
          </div>
        </form>
      )}

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        exportable
        exportFilename="通路列表"
        emptyMessage="沒有通路資料"
      />
    </div>
  );
}
